import {
  AEM_SCHOOL_SCOPED_ROLES,
  AemProgramsRoleIds,
  AemRoleIds,
  AffiliationHeadRoleIds,
  AllAemRoleIds,
  AllAffiliationRoleIds,
} from "@/constants/userRoles";
import { AffiliationSelectorStateEnum } from "@/enums/affiliation";
import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";

export default [
  { path: "/aem", redirect: { name: "AemMySchools" } },
  {
    path: "/aem/my-schools",
    component: () => import("@/views/aem/AemMySchoolsSection.vue"),
    meta: {
      requiresAuth: true,
      allowedRoleIds: AEM_SCHOOL_SCOPED_ROLES,
      scrollToTop: true,
    },
    children: [
      {
        path: "",
        name: "AemMySchools",
        component: () => import("@/views/aem/AemMySchools.vue"),
        meta: {
          requiresAuth: true,
          allowedRoleIds: AemRoleIds,
          scrollToTop: true,
        },
      },
      {
        path: ":affiliationId/lifecycle",
        name: "AemSchoolLifecycle",
        component: () => import("@/views/aem/AemSchoolLifecycle.vue"),
        meta: {
          requiresAuth: true,
          allowedRoleIds: AEM_SCHOOL_SCOPED_ROLES,
          scrollToTop: true,
        },
      },
      {
        path: ":affiliationId/onboarding",
        name: "AemOnboardingChecklist",
        component: () => import("@/views/aem/AemOnboardingChecklist.vue"),
        meta: {
          requiresAuth: true,
          allowedRoleIds: AEM_SCHOOL_SCOPED_ROLES,
          scrollToTop: true,
        },
      },
      {
        path: ":affiliationId/programs",
        name: "AemPrograms",
        component: () => import("@/views/aem/AemProgramsOverview.vue"),
        meta: {
          requiresAuth: true,
          allowedRoleIds: AemProgramsRoleIds,
          scrollToTop: true,
        },
      },
      {
        path: ":affiliationId/programs/:programId",
        name: "AemProgramHub",
        component: () => import("@/views/aem/AemProgramHub.vue"),
        meta: {
          requiresAuth: true,
          allowedRoleIds: AemRoleIds,
          scrollToTop: true,
        },
      },
      {
        path: ":affiliationId/programs/:programId/calendar",
        name: "AemProgramCalendar",
        redirect: (to: RouteLocationNormalized) => ({
          name: "AemProgramHub",
          params: to.params,
          query: { tab: "calendar" },
        }),
        meta: { requiresAuth: true, allowedRoleIds: AemRoleIds },
      },
      {
        path: ":affiliationId/programs/:programId/teaching-plan",
        name: "AemProgramTeachingPlan",
        redirect: (to: RouteLocationNormalized) => ({
          name: "AemProgramHub",
          params: to.params,
          query: { tab: "plan" },
        }),
        meta: { requiresAuth: true, allowedRoleIds: AemRoleIds },
      },
    ],
  },
  {
    path: "/aem/tickets",
    name: "AemTickets",
    component: () => import("@/views/aem/AemTickets.vue"),
    meta: {
      requiresAuth: true,
      allowedRoleIds: AEM_SCHOOL_SCOPED_ROLES,
      scrollToTop: true,
    },
  },
  {
    path: "/aem/tasks",
    name: "AemTasks",
    component: () => import("@/views/aem/AemTasks.vue"),
    meta: {
      requiresAuth: true,
      allowedRoleIds: AemRoleIds,
      scrollToTop: true,
    },
  },
  {
    path: "/aem/visits",
    name: "AemVisits",
    component: () => import("@/views/aem/AemVisits.vue"),
    meta: {
      requiresAuth: true,
      allowedRoleIds: AemRoleIds,
      scrollToTop: true,
    },
  },
  {
    path: "/aem/visits/:visitId",
    name: "AemVisitDetail",
    component: () => import("@/views/aem/AemVisitDetail.vue"),
    meta: {
      requiresAuth: true,
      allowedRoleIds: AemRoleIds,
      scrollToTop: true,
    },
  },
  {
    path: "/aem/assessments",
    name: "AemAssessments",
    component: () => import("@/views/aem/AemAssessments.vue"),
    meta: {
      requiresAuth: true,
      allowedRoleIds: AemRoleIds,
      scrollToTop: true,
    },
  },
  {
    path: "/aem/onboarding",
    name: "AemPrincipalOnboarding",
    component: () => import("@/views/aem/AemPrincipalOnboarding.vue"),
    meta: {
      requiresAuth: true,
      allowedRoleIds: AffiliationHeadRoleIds,
      scrollToTop: true,
    },
  },

  // --- AEM manager / affiliation operations screens ---
  {
    path: "/aem/manage-offline-tests",
    component: () =>
      import(/* webpackChunkName: 'ManageOfflineTests' */ "@/views/aem/ManageOfflineTests.vue"),
    name: "ManageOfflineTests",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds,
      showAffiliationSelector: AffiliationSelectorStateEnum.ENABLED,
    },
  },
  {
    path: "/aem/student-marksheet-reports",
    component: () =>
      import(
        /* webpackChunkName: 'StudentMarksheetReports' */ "@/views/aem/StudentMarksheetReports.vue"
      ),
    name: "StudentMarksheetReports",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds,
      showAffiliationSelector: AffiliationSelectorStateEnum.ENABLED,
    },
  },
  {
    path: "/aem/publish-test-series",
    component: () =>
      import(/* webpackChunkName: 'PublishTestSeries' */ "@/views/aem/PublishTestSeries.vue"),
    name: "PublishTestSeries",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds,
      showAffiliationSelector: AffiliationSelectorStateEnum.ENABLED,
    },
  },
  {
    path: "/aem/affiliation-detailed-review",
    component: () =>
      import(
        /* webpackChunkName: 'AffiliationDetailedReview' */ "@/views/aem/AffiliationDetailedReview.vue"
      ),
    name: "AffiliationDetailedReview",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds.concat(AllAffiliationRoleIds),
      showAffiliationSelector: AffiliationSelectorStateEnum.ENABLED,
    },
  },
  {
    path: "/aem/batch-teacher-mapping",
    component: () =>
      import(/* webpackChunkName: 'BatchTeacherMapping' */ "@/views/aem/BatchTeacherMapping.vue"),
    name: "BatchTeacherMapping",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds,
      showAffiliationSelector: AffiliationSelectorStateEnum.ENABLED,
    },
  },
  {
    path: "/aem/affiliation-connects",
    component: () =>
      import(/* webpackChunkName: 'AffiliationConnects' */ "@/views/aem/AffiliationConnects.vue"),
    name: "AffiliationConnects",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds,
    },
  },
  {
    path: "/aem/affiliation-monthly-meetings",
    component: () =>
      import(
        /* webpackChunkName: 'AffiliationMonthlyMeetings' */ "@/views/aem/AffiliationMonthlyMeetings.vue"
      ),
    name: "AffiliationMonthlyMeetings",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds,
    },
  },
  {
    path: "/aem/affiliation-pdf-reports",
    component: () =>
      import(
        /* webpackChunkName: 'AffiliationPdfReports' */ "@/views/aem/AffiliationPdfReports.vue"
      ),
    name: "AffiliationPdfReports",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds,
      showAffiliationSelector: AffiliationSelectorStateEnum.ENABLED,
    },
  },
  {
    path: "/aem/affiliation-queries",
    component: () =>
      import(/* webpackChunkName: 'AffiliationQueries' */ "@/views/aem/AffiliationQueries.vue"),
    name: "AffiliationQueries",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AllAemRoleIds,
    },
  },
  // ─── Olympiad operations ────────────────────────────────────────────────────
  // Registered in aemRoutes rather than adminRoutes because AemRoleIds is exactly the handoff's
  // "Admin/AEM" set — one module serves both audiences without a duplicated guard.
  // The single-student (B2C) form is a modal on the bulk-students screen, not a route.
  // None of the olympiad routes carry showAffiliationSelector: the global school picker lists
  // every school in the system, registered or not. The bulk-students and roster screens pick
  // their school through OlympiadBatchPicker instead, which lists only the schools registered
  // in the chosen olympiad.
  {
    path: "/aem/olympiad/onboard-schools",
    component: () =>
      import(
        /* webpackChunkName: 'OlympiadOnboardSchools' */ "@/views/olympiad/OlympiadOnboardSchools.vue"
      ),
    name: "OlympiadOnboardSchools",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AemRoleIds,
    },
  },
  {
    path: "/aem/olympiad/register-students-bulk",
    component: () =>
      import(
        /* webpackChunkName: 'OlympiadBulkStudents' */ "@/views/olympiad/OlympiadBulkStudents.vue"
      ),
    name: "OlympiadBulkStudents",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AemRoleIds,
    },
  },
  {
    // No param: the screen carries an olympiad picker and loads nothing until one is chosen.
    path: "/aem/olympiad/tracking",
    component: () =>
      import(/* webpackChunkName: 'OlympiadTracking' */ "@/views/olympiad/OlympiadTracking.vue"),
    name: "OlympiadTracking",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AemRoleIds,
    },
  },
  {
    path: "/aem/olympiad/students",
    component: () =>
      import(
        /* webpackChunkName: 'OlympiadRegisteredStudents' */ "@/views/olympiad/OlympiadRegisteredStudents.vue"
      ),
    name: "OlympiadRegisteredStudents",
    meta: {
      requiresAuth: true,
      scrollToTop: true,
      allowedRoleIds: AemRoleIds,
    },
  },
] as unknown as Array<RouteRecordRaw>;
